import type { ScoClarityReviewSync, ScoSessionReview, ScoSessionReviewAuthor } from '@src/graphql-types'

/** Max reviews the reviewee may feature (L40; backend `SESSION_REVIEW_FEATURE_LIMIT`). */
export const SESSION_REVIEW_FEATURE_LIMIT = 3

export const SESSION_REVIEW_SOURCE_STARTUPS = 'startups'
export const SESSION_REVIEW_SOURCE_CLARITY = 'clarity'

/** The review fields the display components read. Pass query items straight through. */
export type SessionReviewItem = Pick<
  ScoSessionReview,
  'id' | 'score' | 'text' | 'createdAt' | 'sourceKey' | 'isFeatured' | 'isTextHidden'
> &
  Partial<Pick<ScoSessionReview, 'isHidden' | 'isHiddenByAdmin'>> & {
    author: Pick<ScoSessionReviewAuthor, 'isMember' | 'name' | 'tagline' | 'imageUrl' | 'slug'>
  }

/** "Startups" or "Clarity.fm" (L46: every review is labelled by source). */
export const sessionReviewSourceLabel = (sourceKey?: string | null): string =>
  sourceKey === SESSION_REVIEW_SOURCE_CLARITY ? 'Clarity.fm' : 'Startups'

/**
 * Author display name. A Clarity author with no snapshot name is a "Clarity member"; a Startups
 * author whose account is gone is a "Startups member".
 */
export const sessionReviewAuthorName = (review: Pick<SessionReviewItem, 'author' | 'sourceKey'>): string => {
  const name = review.author?.name?.trim()
  if (name) {
    return name
  }
  return review.sourceKey === SESSION_REVIEW_SOURCE_CLARITY ? 'Clarity member' : 'Startups member'
}

/** `/members/{slug}` only when the backend sent a slug (L45: no slug = viewer can't open it). */
export const sessionReviewAuthorHref = (
  author?: Pick<ScoSessionReviewAuthor, 'slug'> | null
): string | null => (author?.slug ? `/members/${encodeURIComponent(author.slug)}` : null)

const timeOf = (value: unknown): number => {
  if (!value) {
    return 0
  }
  const time = new Date(value as string).getTime()
  return Number.isFinite(time) ? time : 0
}

/**
 * Featured first, then newest (mirrors the backend order). Stable for equal keys. Used when pages
 * are appended locally so a later page can never jump ahead of a featured review.
 */
export const sortSessionReviews = <T extends Pick<SessionReviewItem, 'isFeatured' | 'createdAt'>>(
  reviews: T[]
): T[] =>
  reviews
    .map((review, index) => ({ review, index }))
    .sort((a, b) => {
      if (!!a.review.isFeatured !== !!b.review.isFeatured) {
        return a.review.isFeatured ? -1 : 1
      }
      const byDate = timeOf(b.review.createdAt) - timeOf(a.review.createdAt)
      return byDate !== 0 ? byDate : a.index - b.index
    })
    .map(({ review }) => review)

/** Append a page, de-duped by id, keeping the featured-then-newest order. */
export const mergeSessionReviewPages = <T extends Pick<SessionReviewItem, 'id' | 'isFeatured' | 'createdAt'>>(
  existing: Array<T | null | undefined>,
  incoming: Array<T | null | undefined>
): T[] => {
  const seen = new Set<string>()
  const merged: T[] = []
  for (const review of [...existing, ...incoming]) {
    if (!review || seen.has(String(review.id))) {
      continue
    }
    seen.add(String(review.id))
    merged.push(review)
  }
  return sortSessionReviews(merged)
}

/** One decimal, e.g. "4.8" or "5.0". Null when there is no average. */
export const formatSessionReviewAverage = (averageScore?: number | null): string | null => {
  if (averageScore == null || !Number.isFinite(averageScore)) {
    return null
  }
  return (Math.round(averageScore * 10) / 10).toFixed(1)
}

export const sessionReviewCountLabel = (count?: number | null): string => {
  const n = count ?? 0
  return `${n.toLocaleString('en-US')} review${n === 1 ? '' : 's'}`
}

/** L41: the summary shows from 1 review. */
export const hasSessionReviews = (summary?: { count?: number | null } | null): boolean =>
  (summary?.count ?? 0) > 0

export const featuredSessionReviewCount = (reviews: Array<Pick<SessionReviewItem, 'isFeatured'> | null>) =>
  reviews.filter(review => review?.isFeatured).length

/** Hidden reviews (by the reviewee or by ops) can't be featured; the backend refuses them. */
export const canFeatureSessionReview = (
  review: Pick<SessionReviewItem, 'isFeatured' | 'isHidden' | 'isHiddenByAdmin'>,
  featuredCount: number
): boolean => {
  if (review.isFeatured) {
    return true
  }
  if (review.isHidden || review.isHiddenByAdmin) {
    return false
  }
  return featuredCount < SESSION_REVIEW_FEATURE_LIMIT
}

const ERROR_MESSAGES: Record<string, string> = {
  SESSION_REVIEW_FEATURE_LIMIT: `You can feature up to ${SESSION_REVIEW_FEATURE_LIMIT} reviews. Unfeature one first.`,
  CLARITY_REVIEW_SYNC_UNAVAILABLE: 'Clarity sync is unavailable right now. Try again later.',
  CLARITY_ACCOUNT_NOT_LINKED: 'No Clarity account is connected to your profile.',
  USER_NOT_AUTHORIZED: 'You can only manage reviews about you.',
  USER_NOT_AUTHENTICATED: 'Sign in to manage your reviews.',
  NOT_FOUND: 'That review is no longer available.',
}

/** Map a backend error key (thrown as the message) to copy; fall back for anything else. */
export const sessionReviewErrorMessage = (error: unknown, fallback: string): string => {
  const raw = (error instanceof Error ? error.message : typeof error === 'string' ? error : '') || ''
  const message = raw.replace(/^GraphQL error:\s*/i, '').trim()
  for (const [key, copy] of Object.entries(ERROR_MESSAGES)) {
    if (message === key || message.includes(key)) {
      return copy
    }
  }
  return message || fallback
}

/** "Synced 12 reviews: 3 new, 1 updated, 2 removed." */
export const clarityReviewSyncSummary = (
  sync?: Pick<
    ScoClarityReviewSync,
    'fetchedCount' | 'createdCount' | 'updatedCount' | 'restoredCount' | 'removedCount'
  > | null
): string => {
  if (!sync) {
    return 'Clarity sync finished.'
  }
  const parts = [
    `${sync.createdCount ?? 0} new`,
    `${(sync.updatedCount ?? 0) + (sync.restoredCount ?? 0)} updated`,
    `${sync.removedCount ?? 0} removed`,
  ]
  const fetched = sync.fetchedCount ?? 0
  return `Checked ${fetched} Clarity review${fetched === 1 ? '' : 's'}: ${parts.join(', ')}.`
}
